import { Fragment, useMemo } from 'react'

import { IconLink } from '@tabler/icons-react'
import { isArray, isBoolean, isEmpty, isObject, kebabCase, uniq } from 'lodash'
import type { OpenAPIV3_1 as OpenApi3 } from 'openapi-types'
import ReactMarkdown from 'react-markdown'

import { usePage } from '@subaio/components/src/Router/usePage'
import { isNonNullable } from '@subaio/utils'

import { components } from 'components/DefaultMdxComponents'
import { Section } from 'components/Section'
import { SyntaxHighlighter } from 'components/SyntaxHighlighter'

import { List } from '../Common'
import { useApiContext } from '../context'
import { useDeprecation } from '../Deprecation'
import { Anchor } from './Anchor'
import { Container } from './Container'
import { DeprecatedAlertBox } from './DeprecatedAlertBox'
import { ExpandButton } from './ExpandButton'
import { ParentKey } from './ParentKey'
import { Title } from './Title'
import { Type } from './Type'

const renderExample = (
	example: any,
	type: string | Array<string> | undefined,
	parentType: string | Array<string> | undefined,
) => {
	if (parentType === 'array' && isArray(example)) {
		if (type === 'string') return `["${example.join('", "')}"]`
		return JSON.stringify(example, null, 2)
	}
	if (typeof example === 'boolean') return example ? 'true' : 'false'
	if (type === 'string' || type === 'enum') return `"${example}"`
	if (isObject(example)) return JSON.stringify(example, null, 2)
	return example
}

type UnionObject = {
	type: 'oneOf'
	possibilities: Array<OpenApi3.SchemaObject | OpenApi3.ParameterObject | OpenApi3.HeaderObject>
} & OpenApi3.BaseSchemaObject

type Value =
	| OpenApi3.SchemaObject
	| OpenApi3.ParameterObject
	| OpenApi3.HeaderObject
	| OpenApi3.ReferenceObject
	| UnionObject

type KeysOfUnion<T> = T extends T ? keyof T : never

const getMostValidValue = <T extends Value>(schema: T, childSchema: T, key: KeysOfUnion<T>) => {
	if (key === 'description') {
		if (key in schema && schema[key]) return schema[key] as string | number | boolean | Array<string> | null
		if (key in childSchema && childSchema[key])
			return childSchema[key] as string | number | boolean | Array<string> | null
	}
	if (['minItems', 'maxItems', 'minLength', 'maxLength', 'minimum', 'maximum'].includes(key)) {
		if (key in childSchema && childSchema[key] !== null && schema[key] !== undefined)
			return childSchema[key] as string | number | boolean | Array<string> | null
		if (key in schema && schema[key] !== null && schema[key] !== undefined)
			return schema[key] as string | number | boolean | Array<string> | null
	}
	if (key in childSchema) return childSchema[key] as string | number | boolean | Array<string> | null
	if (key in schema) return schema[key] as string | number | boolean | Array<string> | null
}

type Props = {
	name: string
	parentKey?: string
	value:
		| OpenApi3.SchemaObject
		| OpenApi3.ParameterObject
		| OpenApi3.HeaderObject
		| OpenApi3.ReferenceObject
		| OpenApi3.BaseSchemaObject
	isExpandedInitially?: boolean
	isRequired: boolean | null
	linkPrefix?: string
	shouldIndent?: boolean
}

export const Attribute = ({
	name,
	parentKey,
	value,
	isRequired,
	shouldIndent = false,
	isExpandedInitially = false,
	linkPrefix,
}: Props) => {
	const page = usePage()
	const { getRefValue } = useApiContext()
	const definition = useMemo(():
		| OpenApi3.SchemaObject
		| OpenApi3.ParameterObject
		| OpenApi3.HeaderObject
		| UnionObject
		| undefined => {
		const getOneOfDefinition = ({
			source,
			oneOf,
		}: {
			source: OpenApi3.SchemaObject
			oneOf: Array<OpenApi3.SchemaObject | OpenApi3.ReferenceObject>
		}) => {
			const possibilities = oneOf
				.map((item) => {
					if ('$ref' in item) return getRefValue(item.$ref)
					return item
				})
				.filter(isNonNullable)
			const nonNullablePossibilities = possibilities.filter(
				(possibility) => !('type' in possibility && possibility.type === 'null'),
			)
			const [nonNullablePossibility] = nonNullablePossibilities

			if (
				possibilities.some((possibility) => 'type' in possibility && possibility.type === 'null') &&
				nonNullablePossibilities.length === 1 &&
				nonNullablePossibility &&
				'type' in nonNullablePossibility &&
				nonNullablePossibility.type
			) {
				return {
					...nonNullablePossibility,
					...source,
					type: uniq([
						...(isArray(nonNullablePossibility.type) ? nonNullablePossibility.type : [nonNullablePossibility.type]),
						'null',
					]) as OpenApi3.SchemaObject['type'],
				} as OpenApi3.SchemaObject
			}

			return {
				...source,
				type: 'oneOf',
				discriminator: source.discriminator,
				possibilities,
			} as UnionObject
		}

		let resolvedValue = value
		if ('$ref' in resolvedValue) {
			const schema = getRefValue(resolvedValue.$ref as string)
			if (schema) {
				if ('description' in resolvedValue && resolvedValue.description) {
					resolvedValue = { ...schema, description: resolvedValue.description }
				} else {
					resolvedValue = schema
				}
			}
		}
		if (
			'additionalProperties' in value &&
			value.additionalProperties &&
			typeof value.additionalProperties !== 'boolean'
		) {
			if ('$ref' in value.additionalProperties) return getRefValue(value.additionalProperties?.$ref)
		}
		if ('items' in resolvedValue && resolvedValue.items) {
			if ('$ref' in resolvedValue.items) return getRefValue(resolvedValue.items?.$ref)
			if ('oneOf' in resolvedValue.items && resolvedValue.items.oneOf) {
				return getOneOfDefinition({ source: resolvedValue.items, oneOf: resolvedValue.items.oneOf })
			}
			if ('allOf' in resolvedValue.items && resolvedValue.items.allOf) {
				return resolvedValue.items.allOf
					.map((item) => {
						if ('$ref' in item) return getRefValue(item.$ref)
						return item
					})
					.reduce((a, b) => ({ ...a, ...b }))
			}
		}
		if ('oneOf' in resolvedValue && resolvedValue.oneOf) {
			return getOneOfDefinition({ source: resolvedValue, oneOf: resolvedValue.oneOf })
		}
		if ('allOf' in resolvedValue && resolvedValue.allOf) {
			return resolvedValue.allOf
				.map((item) => {
					if ('$ref' in item) return getRefValue(item.$ref)
					return item
				})
				.reduce((a, b) => ({ ...a, ...b }))
		}
		return resolvedValue
	}, [value, getRefValue])

	const types = useMemo((): Array<string> => {
		if (!definition) return []
		const typesToReturn: Array<string> = []

		const parentType = 'type' in value ? value.type : undefined
		const childType = 'type' in definition ? definition.type : undefined

		if (parentType?.includes('array')) {
			if (childType === 'oneOf') {
				typesToReturn.push('array of different types')
			} else if ('items' in value && value.items && 'type' in value.items && value.items.type) {
				typesToReturn.push(
					`${(isArray(value.items.type) ? value.items.type : [value.items.type]).map((type) => `array of ${type}s`).join(', ')}`,
				)
			} else {
				typesToReturn.push(
					`${(isArray(childType) ? childType : [childType]).map((type) => `array of ${type}s`).join(', ')}`,
				)
			}
		} else if (isArray(childType)) {
			childType.forEach((type) => {
				if (
					type === 'object' &&
					'additionalProperties' in definition &&
					typeof definition.additionalProperties !== 'boolean' &&
					definition.additionalProperties &&
					'type' in definition.additionalProperties
				) {
					typesToReturn.push('record')
				} else {
					typesToReturn.push(type)
				}
			})
		} else if (childType === 'oneOf' && 'possibilities' in definition && definition.possibilities) {
			const possibleTypes = uniq(
				definition.possibilities
					.flatMap((possibility) => {
						if ('type' in possibility && possibility.type)
							return isArray(possibility.type) ? possibility.type : [possibility.type]
						return undefined
					})
					.filter(isNonNullable),
			)
			typesToReturn.push(`one of ${possibleTypes.join(', ')}`)
		} else if (isNonNullable(childType)) {
			typesToReturn.push(childType)
		}

		if ((('nullable' in value && value.nullable) || parentType?.includes('null')) && !typesToReturn.includes('null'))
			typesToReturn.push('null')
		if (!isRequired) typesToReturn.push('(optional)')

		return uniq(typesToReturn.flat())
	}, [definition, isRequired, value])

	const { shouldShow } = useDeprecation(
		definition ? (getMostValidValue(value, definition, 'deprecated') as boolean) : false,
	)

	if (!definition || !shouldShow) return null

	const childLinkPrefix = kebabCase(`${linkPrefix}-${name.toLowerCase()}`)
	const shouldAttributeExpand = page?.fragment?.includes(childLinkPrefix) || isExpandedInitially
	const content = (
		<>
			{(() => {
				const isDeprecated = getMostValidValue(value, definition, 'deprecated')

				if (isDeprecated) {
					return (
						<DeprecatedAlertBox
							propertyDescription={getMostValidValue(value, definition, 'description') as string}
							fieldName={`${parentKey}.${name}`}
						/>
					)
				}
			})()}
			{(() => {
				const description = getMostValidValue(value, definition, 'description') as string
				if (description) {
					return (
						<ReactMarkdown components={components}>
							{description.replace(/DEPRECATED\{\{(?:.*\n*)*\}\}/, '').trimEnd()}
						</ReactMarkdown>
					)
				}
			})()}
			{(() => {
				const format = getMostValidValue(value, definition, 'format')
				if (format)
					return (
						<p>
							Format: <code>{format}</code>
						</p>
					)
			})()}
			{(() => {
				const pattern = getMostValidValue(value, definition, 'pattern')
				if (pattern)
					return (
						<p>
							Pattern: <code>{pattern}</code>
						</p>
					)
			})()}
			{(() => {
				const minItems = getMostValidValue(value, definition, 'minItems')
				if (minItems !== null && minItems !== undefined)
					return (
						<p>
							Min items: <code>{minItems}</code>
						</p>
					)
			})()}
			{(() => {
				const maxItems = getMostValidValue(value, definition, 'maxItems')
				if (maxItems !== null && maxItems !== undefined)
					return (
						<p>
							Max items: <code>{maxItems}</code>
						</p>
					)
			})()}
			{(() => {
				const minLength = getMostValidValue(value, definition, 'minLength')
				if (minLength !== null && minLength !== undefined)
					return (
						<p>
							Min length: <code>{minLength}</code>
						</p>
					)
			})()}
			{(() => {
				const maxLength = getMostValidValue(value, definition, 'maxLength')
				if (maxLength !== null && maxLength !== undefined)
					return (
						<p>
							Max length: <code>{maxLength}</code>
						</p>
					)
			})()}
			{(() => {
				const minimum = getMostValidValue(value, definition, 'minimum')
				if (minimum !== null && minimum !== undefined)
					return (
						<p>
							Minimum: <code>{minimum}</code>
						</p>
					)
			})()}
			{(() => {
				const maximum = getMostValidValue(value, definition, 'maximum')
				if (maximum !== null && maximum !== undefined)
					return (
						<p>
							Maximum: <code>{maximum}</code>
						</p>
					)
			})()}
			{(() => {
				const enumValue = getMostValidValue(value, definition, 'enum')
				if (enumValue)
					return (
						<p>
							Enum: <code>["{isArray(enumValue) ? enumValue.join('", "') : enumValue}"]</code>
						</p>
					)
			})()}
			{(() => {
				const example = getMostValidValue(value, definition, 'example')
				const type = 'type' in value ? value.type : undefined
				const childType = 'type' in definition ? definition.type : undefined
				if (example !== undefined) {
					return (
						<p>
							Example:{' '}
							{isObject(example) ? (
								<SyntaxHighlighter language='json' fontSize={12} showLineNumbers={false}>
									{renderExample(example, childType ?? type, type)}
								</SyntaxHighlighter>
							) : (
								<code>{renderExample(example, childType ?? type, type)}</code>
							)}
						</p>
					)
				}
			})()}
			{'properties' in definition && definition.properties && !isEmpty(definition.properties) && (
				<ExpandButton isExpandedInitially={shouldAttributeExpand}>
					<List>
						{Object.entries(definition.properties as OpenApi3.SchemaObject).map(([key, definitionValue], index) => (
							<Attribute
								key={index}
								name={key}
								parentKey={`${parentKey ?? ''}${name}${'type' in value && value.type === 'array' ? '[0].' : '.'}`}
								value={definitionValue}
								isRequired={
									isBoolean(definition.required) ? definition.required : definition.required?.includes(key) || false
								}
								linkPrefix={childLinkPrefix}
								shouldIndent
							/>
						))}
					</List>
				</ExpandButton>
			)}
			{'additionalProperties' in definition &&
				definition.additionalProperties &&
				typeof definition.additionalProperties !== 'boolean' &&
				!isEmpty(definition.additionalProperties) &&
				'properties' in definition.additionalProperties &&
				definition.additionalProperties.properties &&
				!isEmpty(definition.additionalProperties.properties) && (
					<ExpandButton isExpandedInitially={shouldAttributeExpand}>
						<List>
							{Object.entries(definition.additionalProperties.properties as OpenApi3.SchemaObject).map(
								([key, definitionValue], index) => {
									const schema = definition.additionalProperties as OpenApi3.SchemaObject
									return (
										<Attribute
											key={index}
											name={`[string].${key}`}
											parentKey={`${parentKey ?? ''}${name}${'type' in value && value.type === 'array' ? '[0].' : '.'}`}
											value={definitionValue}
											linkPrefix={childLinkPrefix}
											isRequired={
												isBoolean(schema.required) ? schema.required : schema.required?.includes(key) || false
											}
											shouldIndent
										/>
									)
								},
							)}
						</List>
					</ExpandButton>
				)}
			{'type' in definition && definition.type === 'oneOf' && 'possibilities' in definition && (
				<>
					<span>
						Value can be one of multiple different types
						{definition.discriminator ? (
							<>
								{' '}
								determined by <code>{definition.discriminator.propertyName}</code>
							</>
						) : (
							''
						)}
						.
					</span>
					{definition.possibilities.map((possibility, index) => {
						const matchingDiscriminatorKey = definition.discriminator?.mapping
							? Object.entries(definition.discriminator?.mapping).find(
									([, schema]) =>
										'$schema' in possibility &&
										(possibility.$schema === schema ||
											possibility.$schema?.includes(schema.replace('.yaml', '').split('/')?.at(-1) ?? '')),
								)
							: undefined

						const linkPrefix = kebabCase(
							`${childLinkPrefix}-${definition.discriminator?.propertyName}-of-${matchingDiscriminatorKey?.[0]}`,
						)

						return (
							<Fragment key={index}>
								{'properties' in possibility && possibility.properties && !isEmpty(possibility.properties) && (
									<ExpandButton
										renderTitle={(isExpanded) =>
											isExpanded ? (
												<>
													Hide <code>{definition.discriminator?.propertyName}</code> of{' '}
													<code>{matchingDiscriminatorKey?.[0]}</code>
												</>
											) : (
												<>
													Show <code>{definition.discriminator?.propertyName}</code> of{' '}
													<code>{matchingDiscriminatorKey?.[0]}</code>
												</>
											)
										}
										isExpandedInitially={
											(definition.possibilities.length === 1 && !(parentKey ?? name)) ||
											page?.fragment?.includes(linkPrefix) ||
											isExpandedInitially
										}
									>
										<List>
											{Object.entries(possibility.properties as OpenApi3.SchemaObject).map(
												([key, definitionValue], index) => (
													<Attribute
														key={index}
														name={key}
														parentKey={
															(parentKey ?? name)
																? `${parentKey ?? ''}${name}${'type' in value && value.type === 'array' ? '[0].' : '.'}`
																: undefined
														}
														value={definitionValue}
														isRequired={
															isBoolean(possibility.required)
																? possibility.required
																: (possibility.required?.includes(key) ?? false)
														}
														linkPrefix={linkPrefix}
														shouldIndent
													/>
												),
											)}
										</List>
									</ExpandButton>
								)}
							</Fragment>
						)
					})}
				</>
			)}
		</>
	)

	const parentKeyParts = parentKey ? parentKey.split('.') : []
	const title = (
		<>
			{(parentKeyParts.length > 0 || name) && (
				<>
					{parentKeyParts.length > 0 &&
						parentKeyParts.map((node, index) => (
							<ParentKey key={index}>
								{index !== 0 && index !== parentKeyParts.length - 1 ? '.' : ''}
								{node}
								<wbr />
							</ParentKey>
						))}
					<span>
						{parentKeyParts.length > 0 ? '.' : ''}
						{name}
					</span>
				</>
			)}
			{types.map((type, index) => (
				<Type key={index}>{type}</Type>
			))}
		</>
	)

	return (
		<Container shouldIndent={shouldIndent}>
			<Section
				level={5}
				title={title}
				label={name}
				prefix={linkPrefix}
				renderHeading={({ children, id }) => (
					<Title>
						{children}
						<Anchor name={page!.name} params={page!.params} fragment={id}>
							<IconLink size={16} />
						</Anchor>
					</Title>
				)}
			>
				{content}
			</Section>
		</Container>
	)
}
